import { useState, useEffect } from 'react';
import WhyChooseUs from '@/pages/home/components/WhyChooseUs';
import Testimonials from '@/pages/home/components/Testimonials';
import FAQ from '@/pages/home/components/FAQ';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

interface ServiceArea {
  town: string;
  primary?: boolean;
}

interface CountyGroup {
  county: string;
  color: string;
  towns: ServiceArea[];
}

const serviceAreaGroups: CountyGroup[] = [
  {
    county: 'Morris County',
    color: 'orange',
    towns: [
      { town: 'Florham Park', primary: true },
      { town: 'Madison' },
      { town: 'Chatham' },
      { town: 'Morris Plains' },
      { town: 'Morristown' },
      { town: 'Parsippany' },
      { town: 'East Hanover' },
      { town: 'Whippany' },
      { town: 'Hanover Township' },
      { town: 'Cedar Knolls' },
      { town: 'Pine Brook' },
      { town: 'Denville' },
      { town: 'Mountain Lakes' },
      { town: 'Convent Station' },
    ],
  },
  {
    county: 'Essex County',
    color: 'gray',
    towns: [
      { town: 'Livingston' },
      { town: 'Millburn' },
      { town: 'Short Hills' },
      { town: 'Roseland' },
      { town: 'West Orange' },
      { town: 'Maplewood' },
      { town: 'South Orange' },
    ],
  },
  {
    county: 'Union County',
    color: 'gray',
    towns: [
      { town: 'Summit' },
      { town: 'New Providence' },
      { town: 'Berkeley Heights' },
      { town: 'Springfield' },
      { town: 'Westfield' },
    ],
  },
];

const nj = { '@type': 'State', name: 'New Jersey' };

const schemaJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: 'TurnCraft Property Solutions',
      description:
        'Professional painting, deep cleaning, and apartment turn services in Florham Park, NJ.',
      inLanguage: 'en-US',
    },
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: `${siteUrl}/`,
      name: 'TurnCraft Property Solutions | Painting & Cleaning',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#business` },
      description:
        'TurnCraft Property Solutions offers painting, deep cleaning, and apartment turn services in Florham Park, NJ. Reliable property solutions for residential and commercial clients.',
      inLanguage: 'en-US',
      dateModified: '2026-04-15',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        ],
      },
    },
    {
      '@type': ['LocalBusiness', 'HomeAndConstructionBusiness'],
      '@id': `${siteUrl}/#business`,
      name: 'TurnCraft Property Solutions',
      url: `${siteUrl}/`,
      telephone: '+19087212891',
      image: 'https://static.readdy.ai/image/ce99f1b5503e75f6dd673df1c7525759/c03337f0ce4a7c7ea2c83635b2c2717a.png',
      logo: 'https://static.readdy.ai/image/ce99f1b5503e75f6dd673df1c7525759/c03337f0ce4a7c7ea2c83635b2c2717a.png',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '186 Columbia Turnpike #5-203',
        addressLocality: 'Florham Park',
        addressRegion: 'NJ',
        postalCode: '07932',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.7929,
        longitude: -74.3893,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '07:00',
          closes: '19:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '07:00',
          closes: '16:00',
        },
      ],
      description:
        'TurnCraft Property Solutions offers professional painting, deep cleaning, and apartment turn services for residential and commercial properties in Florham Park, NJ.',
      areaServed: [
        { '@type': 'City', name: 'Florham Park', containedInPlace: nj },
        { '@type': 'City', name: 'Madison', containedInPlace: nj },
        { '@type': 'City', name: 'Chatham', containedInPlace: nj },
        { '@type': 'City', name: 'Morris Plains', containedInPlace: nj },
        { '@type': 'City', name: 'Morristown', containedInPlace: nj },
        { '@type': 'City', name: 'Parsippany', containedInPlace: nj },
        { '@type': 'City', name: 'East Hanover', containedInPlace: nj },
        { '@type': 'City', name: 'Whippany', containedInPlace: nj },
        { '@type': 'City', name: 'Hanover Township', containedInPlace: nj },
        { '@type': 'City', name: 'Cedar Knolls', containedInPlace: nj },
        { '@type': 'City', name: 'Pine Brook', containedInPlace: nj },
        { '@type': 'City', name: 'Denville', containedInPlace: nj },
        { '@type': 'City', name: 'Mountain Lakes', containedInPlace: nj },
        { '@type': 'City', name: 'Convent Station', containedInPlace: nj },
        { '@type': 'City', name: 'Livingston', containedInPlace: nj },
        { '@type': 'City', name: 'Millburn', containedInPlace: nj },
        { '@type': 'City', name: 'Short Hills', containedInPlace: nj },
        { '@type': 'City', name: 'Roseland', containedInPlace: nj },
        { '@type': 'City', name: 'West Orange', containedInPlace: nj },
        { '@type': 'City', name: 'Maplewood', containedInPlace: nj },
        { '@type': 'City', name: 'South Orange', containedInPlace: nj },
        { '@type': 'City', name: 'Summit', containedInPlace: nj },
        { '@type': 'City', name: 'New Providence', containedInPlace: nj },
        { '@type': 'City', name: 'Berkeley Heights', containedInPlace: nj },
        { '@type': 'City', name: 'Springfield', containedInPlace: nj },
        { '@type': 'City', name: 'Westfield', containedInPlace: nj },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Property Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Commercial Painting',
              description:
                'Large-scale interior and exterior painting for offices, retail, and multi-unit buildings in NJ.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Janitorial & Facility Cleaning',
              description:
                'Scheduled and one-time cleaning services for offices, lobbies, and commercial facilities.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Property Make-Ready',
              description:
                'End-to-end make-ready services including painting, cleaning, and touch-ups for commercial properties.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Interior Painting',
              description:
                'Fresh, clean paint jobs for apartments and homes — prepped, primed, and painted to a flawless finish.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Deep Cleaning',
              description:
                'Thorough move-out and move-in cleaning covering kitchens, bathrooms, floors, and every corner.',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Apartment Turns',
              description:
                'Full unit turnovers between tenants — fast, reliable, and move-in ready.',
            },
          },
        ],
      },
    },
  ],
});

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJsonLd }}
      />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/ce99f1b5503e75f6dd673df1c7525759/c03337f0ce4a7c7ea2c83635b2c2717a.png"
                alt="Company Logo"
                className="h-10 w-10 lg:h-12 lg:w-12 object-contain"
              />
              <span className={`text-xl lg:text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                TurnCraft Property Solutions
              </span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-8 lg:gap-12">
              <a
                href="#home"
                className={`text-sm lg:text-base font-medium transition-all hover:opacity-70 relative group ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full"></span>
              </a>
              <a
                href="#about"
                className={`text-sm lg:text-base font-medium transition-all hover:opacity-70 relative group ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                About Us
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full"></span>
              </a>
              <a
                href="#services"
                className={`text-sm lg:text-base font-medium transition-all hover:opacity-70 relative group ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                Services
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full"></span>
              </a>
              <a
                href="#service-areas"
                className={`text-sm lg:text-base font-medium transition-all hover:opacity-70 relative group ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                Areas
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full"></span>
              </a>
              <a
                href="#contact"
                className={`text-sm lg:text-base font-medium transition-all hover:opacity-70 relative group ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full"></span>
              </a>
            </div>

            {/* CTA Button */}
            <a
              href="#contact"
              className="hidden md:flex items-center gap-2 bg-gray-900 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </a>

            {/* Mobile Menu Button */}
            <button className={`md:hidden text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              <i className="ri-menu-line"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center">
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
          <img
            src="https://static.readdy.ai/image/ce99f1b5503e75f6dd673df1c7525759/c03337f0ce4a7c7ea2c83635b2c2717a.png"
            alt="Company Logo"
            className="w-full h-full object-contain opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Making Every Property Ready for What’s Next
            </h1>
            <p className="text-lg lg:text-xl text-white/85 mb-10 leading-relaxed">
              We deliver reliable property solutions that help owners and managers keep their units clean, refreshed, and move-in ready. Partner with TurnCraft Property Solutions for quality service you can trust.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full text-base font-medium hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
              >
                Get Started
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#about"
                className="flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Left Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-diamond-line text-orange-500"></i>
                <span className="text-xs uppercase tracking-wider text-orange-500 font-semibold">
                  About Our Company
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Quality You Can Turn To.
              </h2>
              <div className="w-16 h-0.5 bg-orange-500"></div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-3 space-y-6">
              {/* Stat Card 2 */}
              <div className="bg-orange-500 rounded-2xl p-8 lg:p-10 relative">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base lg:text-lg text-white font-medium leading-relaxed">
                      TurnCraft Property Solutions is dedicated to helping property owners and managers keep their spaces in top condition. We take pride in delivering reliable, high-quality painting, cleaning, and apartment turn services with attention to detail and care. Our team is committed to delivering clean, refreshed, and ready-to-use spaces for every client&nbsp;while building lasting relationships through professionalism, trust, and consistent results.
                    </p>
                  </div>
                  <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-900 hover:scale-110 transition-transform cursor-pointer flex-shrink-0 ml-4">
                    <i className="ri-arrow-right-up-line text-xl"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Services Section */}
      <section id="services" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-service-line text-gray-900"></i>
              <span className="text-xs uppercase tracking-wider text-gray-900 font-semibold">
                What We Offer
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
              Reliable property solutions for both residential and commercial spaces — delivered with quality and care.
            </p>
          </div>

          {/* Commercial Category */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-900 rounded-full">
                <i className="ri-building-2-line text-white text-lg"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Commercial</h3>
              <div className="flex-1 h-px bg-gray-200 ml-2"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Commercial Card 1 - Image */}
              <div className="relative rounded-3xl overflow-hidden h-64 group cursor-pointer">
                <img
                  src="https://public.readdy.ai/ai/img_res/e0b80a41adbf8c03842eab861a688212.jpg"
                  alt="Commercial Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h4 className="text-xl font-bold mb-1">Commercial Painting</h4>
                  <p className="text-white/85 text-sm">Large-scale interior and exterior painting for offices, retail, and multi-unit buildings.</p>
                </div>
              </div>

              {/* Commercial Card 2 */}
              <div className="bg-gray-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-line text-2xl text-gray-900"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Janitorial &amp; Facility Cleaning</h4>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Scheduled and one-time cleaning services for offices, lobbies, common areas, and commercial facilities — keeping your space professional.
                </p>
              </div>

              {/* Commercial Card 3 */}
              <div className="bg-gray-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-tools-line text-2xl text-gray-900"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Property Make-Ready</h4>
                <p className="text-gray-600 leading-relaxed text-sm">
                  End-to-end make-ready services for commercial properties — painting, cleaning, and touch-ups to get your space market-ready fast.
                </p>
              </div>
            </div>
          </div>

          {/* Residential Category */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full">
                <i className="ri-home-4-line text-white text-lg"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Residential</h3>
              <div className="flex-1 h-px bg-gray-200 ml-2"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Residential Card 1 */}
              <div className="bg-gray-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-paint-brush-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Interior Painting</h4>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Fresh, clean paint jobs for apartments and homes. We prep, prime, and paint every room to a flawless finish — ready for the next tenant.
                </p>
              </div>

              {/* Residential Card 2 */}
              <div className="bg-gray-50 rounded-3xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6">
                  <i className="ri-sparkling-2-line text-2xl text-orange-500"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Deep Cleaning</h4>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Thorough move-out and move-in cleaning services covering kitchens, bathrooms, floors, and every corner — leaving units spotless.
                </p>
              </div>

              {/* Residential Card 3 - Image */}
              <div className="relative rounded-3xl overflow-hidden h-64 group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=bright%20clean%20modern%20apartment%20interior%20after%20professional%20cleaning%20and%20painting%20service%20with%20white%20walls%20fresh%20paint%20neutral%20tones%20natural%20light%20streaming%20through%20windows%20spotless%20floors%20and%20tidy%20living%20space%20ready%20for%20new%20tenant%20move-in%20ready%20residential%20property&width=600&height=500&seq=res-apt-001&orientation=landscape"
                  alt="Apartment Turn Services"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h4 className="text-xl font-bold mb-1">Apartment Turns</h4>
                  <p className="text-white/85 text-sm">Full unit turnovers between tenants — fast, reliable, and move-in ready.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Service Areas Section */}
      <section id="service-areas" className="py-24 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start mb-16">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-map-pin-2-line text-orange-500"></i>
                <span className="text-xs uppercase tracking-wider text-orange-500 font-semibold">
                  Where We Work
                </span>
              </div>
              <h2
                className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Serving Communities Across Northern NJ
              </h2>
            </div>
            <div className="lg:pt-6">
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                Based in <strong>Florham Park, NJ</strong>, TurnCraft Property Solutions proudly serves property
                owners and managers throughout Morris County, Essex County, and Union County. Whether you need
                apartment turn services, commercial painting, or deep cleaning — we come to you.
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 mt-6 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
              >
                Check Your Area
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* County Groups */}
          <div className="grid md:grid-cols-3 gap-8">
            {serviceAreaGroups.map((group) => (
              <div key={group.county} className="bg-white rounded-3xl p-8">
                {/* County Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div
                    className={`w-9 h-9 flex items-center justify-center rounded-full ${
                      group.color === 'orange' ? 'bg-orange-500' : 'bg-gray-900'
                    }`}
                  >
                    <i className="ri-map-pin-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{group.county}</h3>
                </div>

                {/* Towns */}
                <ul className="space-y-2">
                  {group.towns.map((area) => (
                    <li key={area.town} className="flex items-center gap-2.5">
                      {area.primary ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0"></span>
                      )}
                      <span
                        className={`text-sm ${
                          area.primary ? 'font-semibold text-gray-900' : 'text-gray-600'
                        }`}
                      >
                        {area.town}
                        {area.primary && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-orange-500 font-semibold">
                            HQ
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom CTA strip */}
          <div className="mt-12 bg-gray-900 rounded-3xl px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-white font-semibold text-lg">Don&apos;t see your town listed?</p>
              <p className="text-gray-400 text-sm mt-1">
                We may still be able to help. Reach out and we&apos;ll let you know.
              </p>
            </div>
            <a
              href="#contact"
              className="flex items-center gap-2 bg-orange-500 text-white px-8 py-3.5 rounded-full text-sm font-medium hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* Contact Section */}
      <section id="contact" className="bg-gray-900 rounded-t-[40px] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">

          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-phone-line text-orange-500"></i>
              <span className="text-xs uppercase tracking-wider text-orange-500 font-semibold">
                Get In Touch
              </span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-light text-white leading-tight mb-5">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-400 max-w-xl mx-auto mb-8">
              Give us a call or send us an email. We&apos;ll get back to you quickly and get your property on the schedule.
            </p>
            <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 text-orange-400 px-5 py-2.5 rounded-full text-sm font-medium">
              <i className="ri-check-line text-orange-400"></i>
              No obligation &mdash; free estimate
            </div>
          </div>

          {/* CTA Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">
            {/* Call Card */}
            <a
              href="tel:9087212891"
              className="group bg-white/5 hover:bg-orange-500 border border-white/10 hover:border-orange-500 rounded-3xl p-10 flex flex-col items-center text-center transition-all duration-300 cursor-pointer"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500 group-hover:bg-white rounded-full mb-6 transition-colors duration-300">
                <i className="ri-phone-fill text-white group-hover:text-orange-500 text-2xl transition-colors duration-300"></i>
              </div>
              <span className="text-xs uppercase tracking-wider text-gray-400 group-hover:text-white/80 font-semibold mb-3 transition-colors">
                Call Us
              </span>
              <span className="text-2xl lg:text-3xl text-white font-semibold group-hover:text-white transition-colors">
                (908) 721-2891
              </span>
              <span className="text-sm text-gray-500 group-hover:text-white/70 mt-3 transition-colors">
                Mon–Fri 7am–7pm &nbsp;·&nbsp; Sat 7am–4pm
              </span>
            </a>

            {/* Email Card */}
            <a
              href="mailto:turncraftsolutions@gmail.com"
              className="group bg-white/5 hover:bg-orange-500 border border-white/10 hover:border-orange-500 rounded-3xl p-10 flex flex-col items-center text-center transition-all duration-300 cursor-pointer"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-white/10 group-hover:bg-white rounded-full mb-6 transition-colors duration-300">
                <i className="ri-mail-fill text-white group-hover:text-orange-500 text-2xl transition-colors duration-300"></i>
              </div>
              <span className="text-xs uppercase tracking-wider text-gray-400 group-hover:text-white/80 font-semibold mb-3 transition-colors">
                Email Us
              </span>
              <span className="text-base text-white font-semibold group-hover:text-white transition-colors">
                turncraftsolutions@gmail.com
              </span>
              <span className="text-sm text-gray-500 group-hover:text-white/70 mt-3 transition-colors">
                We typically reply within a few hours
              </span>
            </a>
          </div>

          {/* Bottom Info Row */}
          <div className="grid md:grid-cols-3 gap-8 border-t border-white/10 pt-12 mb-12">
            {/* Address */}
            <div className="text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-4">
                <i className="ri-map-pin-line text-orange-500 text-lg"></i>
              </div>
              <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 block">
                Mailing Address
              </span>
              <p className="text-white text-sm leading-relaxed">
                186 Columbia Turnpike #5-203<br />
                Florham Park, NJ 07932
              </p>
            </div>

            {/* Hours */}
            <div className="text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-orange-500 text-lg"></i>
              </div>
              <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 block">
                Business Hours
              </span>
              <p className="text-white text-sm leading-relaxed">
                Mon – Fri: 7:00 AM – 7:00 PM<br />
                Saturday: 7:00 AM – 4:00 PM<br />
                Sunday: Closed
              </p>
            </div>

            {/* Map Link */}
            <div className="text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-4">
                <i className="ri-map-2-line text-orange-500 text-lg"></i>
              </div>
              <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 block">
                Find Us
              </span>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=186+Columbia+Turnpike+Florham+Park+NJ+07932"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer mt-1"
              >
                <i className="ri-navigation-line"></i>
                Get Directions
              </a>
            </div>
          </div>

          {/* Mobile Sticky Call Button */}
      <a
        href="tel:9087212891"
        className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-orange-500 text-white px-7 py-3.5 rounded-full text-base font-semibold whitespace-nowrap cursor-pointer active:scale-95 transition-transform"
        style={{ boxShadow: '0 4px 24px rgba(249,115,22,0.45)' }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-phone-fill text-lg"></i>
        </div>
        Call Now — (908) 721-2891
      </a>

      {/* Footer Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">


              {/* Copyright */}
              <p className="text-gray-500 text-sm">
                © 2025 TurnCraft Property Solutions. All rights reserved.
              </p>

              {/* Policy Links */}
              <div className="flex items-center gap-5">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">
                  Terms
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
